import { useEffect, useRef, useState } from "react";
import { Heart, LoaderCircle, Search, Trash2, Upload, WandSparkles } from "lucide-react";
import client, { absoluteUrl } from "../api/client";
import useImageCatalog from "../hooks/useImageCatalog";
import { capitalise } from "../utils/text";
import { ASSET_MIME } from "./BoardCanvas";
import { ACCEPTED_TYPES, MAX_UPLOAD_MB, catalogAsset, uploadAsset } from "./useUploads";

function AssetThumb({ asset, onPick, onDelete, onRemoveBackground, removing = false }) {
  const name = asset.name || "Image";
  return (
    <div className={`mb-asset${asset.transparent ? " is-transparent" : ""}`}>
      {/* A div rather than a <button>: Firefox will not start a drag from a button. */}
      <div
        role="button"
        tabIndex={0}
        className="mb-asset-pick"
        draggable
        title={`Add ${name}`}
        aria-label={`Add ${name}`}
        onClick={() => onPick(asset)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onPick(asset);
          }
        }}
        onDragStart={(event) => {
          event.dataTransfer.setData(ASSET_MIME, JSON.stringify(asset));
          event.dataTransfer.effectAllowed = "copy";
        }}
      >
        {asset.url ? (
          <img src={absoluteUrl(asset.url)} alt="" loading="lazy" draggable={false} />
        ) : (
          <span className="mb-asset-missing">{name}</span>
        )}
      </div>
      {onRemoveBackground && (
        <button
          type="button"
          className="mb-asset-action mb-asset-wand"
          onClick={() => onRemoveBackground(asset)}
          disabled={removing}
          aria-label={`Remove background from ${name}`}
          title="Remove background"
        >
          {removing ? <LoaderCircle size={13} strokeWidth={1.8} className="mb-spin" /> : <WandSparkles size={13} strokeWidth={1.8} />}
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          className="mb-asset-action mb-asset-delete"
          onClick={() => onDelete(asset)}
          aria-label={`Delete ${name}`}
          title="Delete"
        >
          <Trash2 size={13} strokeWidth={1.8} />
        </button>
      )}
    </div>
  );
}

function LibraryTab({ onPick }) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    client
      .get("/api/images/categories")
      .then(({ data }) => setCategories(data.categories || []))
      .catch((err) => console.error("Categories failed:", err));
  }, []);

  const { items, loading, loadingMore, hasMore, loadMore, error } = useImageCatalog({
    category,
    search,
    favoritesOnly,
    pageSize: 30,
  });
  const scrollRef = useRef(null);
  const endRef = useRef(null);

  // Loads the next page as the list nears its end; set up again after each page
  // so a list too short to scroll keeps filling.
  useEffect(() => {
    const root = scrollRef.current;
    const end = endRef.current;
    if (!root || !end || !hasMore || loadingMore || loading) return undefined;
    const observer = new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && loadMore(), {
      root,
      rootMargin: "300px",
    });
    observer.observe(end);
    return () => observer.disconnect();
  }, [items.length, hasMore, loadingMore, loading, loadMore]);

  return (
    <>
      <div className="mb-panel-filters">
        <label className="mb-search">
          <Search size={14} strokeWidth={1.8} aria-hidden="true" />
          <input
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search artwork"
            aria-label="Search artwork"
          />
        </label>
        <div className="mb-filter-row">
          <select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Category">
            <option value="all">All categories</option>
            {categories.map((item) => (
              <option key={item.key} value={item.key}>
                {capitalise(item.label)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={`mb-chip${favoritesOnly ? " active" : ""}`}
            onClick={() => setFavoritesOnly((value) => !value)}
            aria-pressed={favoritesOnly}
            title="Favourites only"
          >
            <Heart size={13} strokeWidth={1.8} fill={favoritesOnly ? "currentColor" : "none"} />
          </button>
        </div>
      </div>

      <div className="mb-panel-scroll" ref={scrollRef}>
        {loading ? (
          <p className="mb-muted">Loading artwork...</p>
        ) : error ? (
          <p className="mb-muted">{error}</p>
        ) : !items.length ? (
          <p className="mb-muted">No artwork matches.</p>
        ) : (
          <>
            <div className="mb-asset-grid">
              {items.map((image) => (
                <AssetThumb key={image.file_id} asset={catalogAsset(image)} onPick={onPick} />
              ))}
            </div>
            <span ref={endRef} aria-hidden="true" />
            {hasMore && (
              <button type="button" className="mb-load-more" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Loading..." : "Load more"}
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}

function UploadsTab({ uploads, onPick }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length) uploads.upload(files);
  };

  const confirmDelete = (asset) => {
    if (window.confirm("Delete this image? Boards that use it will show an empty slot.")) {
      uploads.remove(asset.ref);
    }
  };

  return (
    <div
      className={`mb-panel-scroll mb-dropzone${dragging ? " is-dragging" : ""}`}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => !event.currentTarget.contains(event.relatedTarget) && setDragging(false)}
      onDrop={(event) => {
        if (!event.dataTransfer.files?.length) return;
        event.preventDefault();
        setDragging(false);
        handleFiles(event.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        multiple
        hidden
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = "";
        }}
      />
      <button type="button" className="mb-upload-button" onClick={() => inputRef.current?.click()}>
        <Upload size={15} strokeWidth={1.8} aria-hidden="true" />
        {uploads.pending ? `Uploading ${uploads.pending}...` : "Upload images"}
      </button>
      <p className="mb-muted mb-upload-hint">
        JPEG, PNG or WebP up to {MAX_UPLOAD_MB} MB. You can also drop files here or straight onto the board.
        {uploads.backgroundRemoval && " Use the wand on an image to cut out its background."}
        {uploads.limit != null && (
          <>
            {" "}
            {uploads.items.length} / {uploads.limit} used.
          </>
        )}
      </p>

      {uploads.loading ? (
        <p className="mb-muted">Loading your images...</p>
      ) : !uploads.items.length ? (
        <p className="mb-muted">You have not uploaded any images yet.</p>
      ) : (
        <div className="mb-asset-grid">
          {uploads.items.map((upload) => (
            <AssetThumb
              key={upload.id}
              asset={uploadAsset(upload)}
              onPick={onPick}
              onDelete={confirmDelete}
              onRemoveBackground={uploads.backgroundRemoval ? (asset) => uploads.removeBackground(asset.ref) : null}
              removing={uploads.removing === upload.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AssetPanel({ uploads, onPick }) {
  const [tab, setTab] = useState("library");

  return (
    <aside className="mb-panel mb-assets" aria-label="Images">
      <div className="mb-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "library"}
          className={tab === "library" ? "active" : ""}
          onClick={() => setTab("library")}
        >
          Library
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "uploads"}
          className={tab === "uploads" ? "active" : ""}
          onClick={() => setTab("uploads")}
        >
          My uploads
        </button>
      </div>
      {tab === "library" ? <LibraryTab onPick={onPick} /> : <UploadsTab uploads={uploads} onPick={onPick} />}
    </aside>
  );
}
